/// List of all universal storage keys related to registration.
import date from '@/components/kit/date'

const stateKeys = [
  'serial',
  'material',
  'source',
  'scan-units-start.indoor-type',
  'scan-units-start.indoor-type-image',
  'scan-units.scanned',
  'scan-units.existing',
  'scan-units.code',
  'scan-units.installation',
  'scan-units.product-range',
  'scan-units.user-picked-product-range-id',
  'scan-units.unit-filter',
  'scan-units.unit-type-hint',
  'scan-units.multiple-product-ranges',
  'scan-units.gateway-device-id',
  'commissioning.date',
  'building.date',
  'end-user.personal-info',
  'end-user.address',
  'service-partner',
  'commissioning-code',
  'commissioning-code-invitation-response',
  'search-selection'
]

export const actions = {
  /// Clears any registration state
  clearRegistration() {
    for (let ix = 0; ix < stateKeys.length; ix++) {
      this.$storage.removeUniversal(stateKeys[ix])
    }
  },
  clearUnitInputState() {
    this.$storage.removeUniversal('search-selection')
    this.$storage.removeUniversal('serial')
    this.$storage.removeUniversal('material')
    this.$storage.removeUniversal('source')
  },
  clearCommissioningCodeInvitationState() {
    this.$storage.removeUniversal('commissioning-code-invitation-response')
  },
  setUnitInputFilter(_, unit) {
    this.$storage.setUniversal('scan-units.unit-type-hint', unit.type)

    // Check first if we have still multiple product ranges & the user did not pick one
    const multiple = this.$storage.getUniversal(
      'scan-units.multiple-product-ranges'
    )
    const pickedProductRangeId = this.$storage.getUniversal(
      'scan-units.user-picked-product-range-id'
    )
    if (multiple && multiple.length > 1 && !pickedProductRangeId) {
      // We have multiple product ranges & the user did not picked one
      // Retrieve all the possible unit types with their possible material codes from all product ranges
      const allInstallationUnits = multiple.map(
        (elem, index) => elem.installation_units
      )
      // Merge it to one array
      let allUnitTypeTemplates = []
      for (let i = 0; i < allInstallationUnits?.length; i++) {
        allUnitTypeTemplates = allUnitTypeTemplates.concat(
          allInstallationUnits[i]
        )
      }
      // Filter out the necessary type
      const requiredUnitTypeTemplates = allUnitTypeTemplates.filter((u) => {
        return u.type === unit.type
      })
      if (requiredUnitTypeTemplates.length === 0) {
        // incorrect behaviour, the unit type is not present in the product range this should not happen
        console.error(
          `Attempt to filter for unit type ${unit.type} which is unknown to the multiple product ranges. Only known unit types can be added.`
        )
        this.$storage.removeUniversal('scan-units.unit-filter')
        return
      }

      // Merge all the material codes of the necessary unit type into one array
      let materialCodes = []
      for (let i = 0; i < requiredUnitTypeTemplates.length; i++) {
        materialCodes = materialCodes.concat(
          requiredUnitTypeTemplates[i].material_codes
        )
      }
      let materials = []
      materials = materialCodes.map((elem) => {
        return {
          material_code: elem
        }
      })
      this.$storage.setUniversal('scan-units.unit-filter', materials)
    } else {
      // We have no multiple product ranges, or the user already picked one
      const productRange = this.$storage.getUniversal(
        'scan-units.product-range'
      )
      if (!productRange) {
        // can't set a filter without a product range
        this.$storage.removeUniversal('scan-units.unit-filter')
        return
      }

      const unitTypeTemplate = productRange.installation_units.filter(
        (u) => u.type === unit.type
      )[0]

      if (!unitTypeTemplate) {
        // incorrect behaviour, the unit type is not present in the product range this should not happen
        console.error(
          `Attempt to filter for unit type ${unit.type} which is unknown to the current product range. Only known unit types can be added.`
        )
        this.$storage.removeUniversal('scan-units.unit-filter')
        return
      }

      const materials = unitTypeTemplate.material_codes.map((elem) => {
        return {
          material_code: elem
        }
      })
      this.$storage.setUniversal('scan-units.unit-filter', materials)
    }
  },
  setUnitType(_, { unitType, unitImage }) {
    this.$storage.setUniversal('scan-units-start.indoor-type', unitType)
    this.$storage.setUniversal('scan-units-start.indoor-type-image', unitImage)
  },
  addScannedUnit(_, { serial, material }) {
    // get the list of scanned units
    let units = this.$storage.getUniversal('scan-units.scanned') || []

    // ensure we don't duplicate a material + serial combination
    units = units.filter(
      (u) => !(u.material === material && u.serial === serial)
    )

    units.push({ material, serial })

    // update the list of scanned units
    this.$storage.setUniversal('scan-units.scanned', units)
  },
  /// Returns a list of units which need to be appended to validation requests.
  /// Such units include units provided by the user so far, excluding units present in installation details.
  getUnitsToValidate() {
    const scannedUnits = this.$storage.getUniversal('scan-units.scanned') || []
    const installation = this.$storage.getUniversal(
      'scan-units.installation'
    ) || { units: [] }
    return scannedUnits.filter((u) =>
      installation.units.filter(
        (iu) => !(iu.material === u.material && iu.serial === u.serial).length
      )
    )
  },
  /// Returns `true` if the given unit is already a member of a confirmed installation.
  getUnitIsAlreadyConfirmed(_, { material, serial }) {
    const installation = this.$storage.getUniversal(
      'scan-units.installation'
    ) || { units: [] }
    const match = installation.units.filter(
      (iu) => iu.material === material && iu.serial === serial
    )[0]
    if (match) {
      return true
    }
    return false
  },
  getScannedAndInstalledUnits() {
    // get the list of scanned units
    const scannedUnits = this.$storage.getUniversal('scan-units.scanned') || []
    const installation = this.$storage.getUniversal(
      'scan-units.installation'
    ) || { units: [], product_range: {} }
    const scannedAndInstalledUnits = []
    // we first add the units from the installation so the readonly flag can be set
    for (const unit of installation.units) {
      if (
        !scannedAndInstalledUnits.filter(
          (u) => u.material === unit.material && u.serial === unit.serial
        ).length
      ) {
        scannedAndInstalledUnits.push({ ...unit, readOnly: true })
      }
    }

    for (const unit of scannedUnits) {
      // only add new units
      if (
        !scannedAndInstalledUnits.filter(
          (u) => u.material === unit.material && u.serial === unit.serial
        ).length
      ) {
        scannedAndInstalledUnits.push({ ...unit })
      }
    }
    return scannedAndInstalledUnits
  },
  /// Returns a collection of units + unit slots for the registration overview.
  /// This includes already scanned units, units from a recognized installation + slots for extra units from the product range.
  /// This method is responsible for recalculating and storing the current product range.
  /// We dod this every time because given changes to the scanned units we must provide an adapted result.
  async getUnitsForOverview({ dispatch }, preferredProductRangeId) {
    const scannedAndInstalledUnits = await dispatch(
      'getScannedAndInstalledUnits'
    )
    if (!scannedAndInstalledUnits || !scannedAndInstalledUnits?.length) {
      // to prevent an error on $marlonApi.productRangeSearch
      return []
    }
    const country =
      this.$storage.getUniversal('gigyaProfile') &&
      this.$storage.getUniversal('gigyaProfile').country
    const language =
      this.$storage.getUniversal('language') &&
      this.$storage.getUniversal('language').substr(0, 2)
    const searchResponse = await this.$marlonApi.productRangeSearch(
      scannedAndInstalledUnits,
      country,
      language
    )

    if (!searchResponse) {
      // API call failed
      return []
    }

    // choose a product range, take into account the requested preference, if possible
    let productRange = searchResponse?.product_ranges?.filter(
      (p) => p.id === preferredProductRangeId
    )[0]

    if (!productRange) {
      // if we've previously selected a product range, try to use it again
      const alreadySelected = this.$storage.getUniversal(
        'scan-units.product-range'
      )
      if (alreadySelected) {
        productRange = searchResponse.product_ranges?.filter(
          (p) => p.id === alreadySelected.id
        )[0]
      }
    }

    // if the search results are narrowed down to 1 product range, then use that one
    if (!productRange && searchResponse.product_ranges?.length === 1) {
      productRange = searchResponse.product_ranges[0]
    }

    if (!searchResponse.product_ranges?.length) {
      // no or unknown product range, we only show the installed units
      const unitReadOnly = scannedAndInstalledUnits
        .filter((u) => u.readOnly)
        .map((u) => {
          return {
            type: u.type,
            material: u.material,
            serial: u.serial,
            required: true,
            minimNumber: 1,
            maximumNumber: 1
          }
        })
      return unitReadOnly || []
    }
    this.$storage.setUniversal(
      'scan-units.multiple-product-ranges',
      searchResponse.product_ranges
    )

    // update the product range we are using for this installation
    this.$storage.setUniversal('scan-units.product-range', productRange)

    // get all the units that we can use to show in the overview
    let allInstallationUnits = []
    if (productRange) {
      allInstallationUnits = productRange.installation_units
    } else {
      // merge multiple arrays in one
      for (const myProductRange of searchResponse.product_ranges) {
        for (const installationUnits of myProductRange.installation_units) {
          const installationUnitType = allInstallationUnits.find(
            (iu) => iu.type === installationUnits.type
          )
          if (installationUnitType) {
            allInstallationUnits.map((x) => {
              if (x.type === installationUnits.type) {
                return {
                  ...x,
                  material_codes: x.material_codes
                    .concat(installationUnits.material_codes)
                    .filter(
                      (value, index, self) => self.indexOf(value) === index
                    ),
                  maximum_number: Math.max(
                    x.maximum_number,
                    installationUnits.maximum_number
                  ),
                  required: x.required || installationUnits.required
                }
              }
            })
          } else {
            allInstallationUnits.push(installationUnits)
          }
        }
      }
    }

    // apply unit types from the product ranges.
    // units coming from existing installations already have their type set
    for (let i = 0; i < scannedAndInstalledUnits.length; i++) {
      const unit = scannedAndInstalledUnits[i]
      if (!unit.type) {
        const matchingTemplate = allInstallationUnits.filter((t) =>
          t.material_codes.includes(unit.material)
        )[0]
        if (matchingTemplate) {
          unit.type = matchingTemplate.type
        }
      }
    }

    const result = []
    for (const templateUnits of allInstallationUnits) {
      const scannedOrInstalledOfType = scannedAndInstalledUnits.filter(
        (u) => u.type === templateUnits.type
      )

      for (
        let typeIndex = 0;
        typeIndex < templateUnits.maximum_number;
        typeIndex++
      ) {
        const alreadyFilledUnit = scannedOrInstalledOfType[typeIndex]
        const unitOptional =
          !templateUnits.required ||
          (templateUnits.maximum_number > 1 && typeIndex > 0)
        if (alreadyFilledUnit) {
          result.push({
            type: templateUnits.type,
            material: alreadyFilledUnit.material,
            serial: alreadyFilledUnit.serial,
            required: !unitOptional,
            readOnly: alreadyFilledUnit.readOnly || false
          })
        } else {
          // empty slot for a unit type
          result.push({
            type: templateUnits.type,
            material: null,
            serial: null,
            required: !unitOptional,
            readOnly: false,
            hintMaterialCodes: templateUnits.material_codes
          })
          // break early so we only reveal 1 extra slot for each type
          break
        }
      }
    }

    return result
  },
  setCommissioningDate(_, date) {
    this.$storage.setUniversal('commissioning.date', date)
  },
  getCommissioningDate() {
    return this.$storage.getUniversal('commissioning.date')
  },
  setBuildDate(_, date) {
    this.$storage.setUniversal('building.date', date)
  },
  getBuildDate() {
    return this.$storage.getUniversal('building.date')
  },
  setPersonalInfo(_, info) {
    this.$storage.setUniversal('end-user.personal-info', info)
  },
  getPersonalInfo() {
    return this.$storage.getUniversal('end-user.personal-info')
  },
  setAddress(_, info) {
    this.$storage.setUniversal('end-user.address', info)
  },
  getAddress() {
    return this.$storage.getUniversal('end-user.address')
  },
  setGatewayDeviceId(_, gatewayDeviceId) {
    this.$storage.setUniversal('scan-units.gateway-device-id', gatewayDeviceId)
  },
  getGatewayDeviceId() {
    return this.$storage.getUniversal('scan-units.gateway-device-id')
  },
  setServicePartner(_, servicePartner) {
    this.$storage.setUniversal('service-partner', servicePartner)
  },
  getServicePartner() {
    return this.$storage.getUniversal('service-partner')
  },
  setCommissioningInvitationResponse(_, response) {
    this.$storage.setUniversal(
      'commissioning-code-invitation-response',
      response
    )
  },
  getCommissioningInvitationResponse() {
    return this.$storage.getUniversal('commissioning-code-invitation-response')
  },
  async registerInstallation({ dispatch, commit }) {
    try {
      const commissionedInstallation = this.$storage.getUniversal(
        'scan-units.installation'
      )
      const productRange = this.$storage.getUniversal(
        'scan-units.product-range'
      )
      const allUnits = await dispatch('getUnitsForOverview', productRange.id)
      const endUserPersonalInfo = await dispatch('getPersonalInfo')
      const endUserAddress = await dispatch('getAddress')
      const servicePartnerInfo = await dispatch('getServicePartner')
      const commissioningDateInfo = await dispatch('getCommissioningDate')
      const buildDateInfo = await dispatch('getBuildDate')
      const gatewayDeviceId = await dispatch('getGatewayDeviceId')
      const commissioningCode = this.$storage.getUniversal('commissioning-code')

      const servicePartner = (servicePartnerInfo || commissionedInstallation)
        .service_partner
      const commissioningDate =
        commissioningDateInfo || commissionedInstallation.commissioning_date
      const buildDate =
        buildDateInfo ||
        (commissionedInstallation &&
          commissionedInstallation.building_finished_date)
      const units = allUnits
        .filter((unit) => unit.material && unit.serial)
        .map((unit) => {
          return {
            type: unit.type,
            material: unit.material,
            serial: unit.serial
          }
        })
      const installationAddress =
        endUserAddress.installation_address ||
        endUserAddress.customer_details.address

      const request = {
        customer_details: {
          ...endUserPersonalInfo,
          ...endUserAddress.customer_details
        },
        installation_address: installationAddress,
        installer_id: servicePartner.id,
        installer_name: servicePartner.full_name,
        commissioning_date: date.toISODateString(commissioningDate),
        build_date: date.toISODateString(buildDate),
        product_range_id: productRange.id,
        commissioning_code: commissioningCode,
        units,
        gateway_device_id: gatewayDeviceId
      }

      const response = await this.$marlonApi.registerInstallation(request)
      return response
    } catch (err) {
      this.$sentry.captureException(err)
      throw err
    }
  },
  async searchMaterialCode(
    { rootState },
    { keyword, launcherStatusIsRegister }
  ) {
    // search from the service
    const country =
      (rootState.gigyaProfile && rootState.gigyaProfile.country) ||
      (this.$storage.getUniversal('gigyaProfile') &&
        this.$storage.getUniversal('gigyaProfile').country)
    const language =
      this.$storage.getUniversal('language') &&
      this.$storage.getUniversal('language').substr(0, 2)

    const materialResults = await this.$marlonApi.searchMaterialCode(
      keyword,
      country,
      language
    )
    const scannedUnits = this.$storage.getUniversal('scan-units.scanned')
    if (
      materialResults?.materialcodes &&
      !scannedUnits &&
      !launcherStatusIsRegister
    ) {
      materialResults.materialcodes = materialResults.materialcodes.filter(
        (m) => m.type !== 'lan_adapter'
      )
    }
    return materialResults
  }
}
